import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
  Tooltip as AriaTooltip,
  TooltipTrigger,
} from 'react-aria-components';

import type { ReactNode } from 'react';

import '../Tooltip/Tooltip.css';
import './IconButton.css';

export interface IconButtonProps extends Omit<AriaButtonProps, 'children' | 'className' | 'style'> {
  /** The icon, e.g. `<X />` from lucide-react. */
  icon: ReactNode;
  /** Required: names the action for screen readers and shows as a tooltip,
   * because an icon alone is a guess for anyone who has not seen it before. */
  label: string;
  variant?: 'ghost' | 'secondary';
  size?: 'sm' | 'md';
}

/** IconButton is a square button whose label is a tooltip. Use it only for
 * universally understood actions (close, more, collapse); anything else gets
 * a Button with a written label. */
export function IconButton({
  icon,
  label,
  variant = 'ghost',
  size = 'md',
  ...props
}: IconButtonProps) {
  return (
    <TooltipTrigger delay={500}>
      <AriaButton
        {...props}
        aria-label={label}
        className="rs-icon-button"
        data-variant={variant}
        data-size={size}
      >
        {icon}
      </AriaButton>
      <AriaTooltip className="rs-tooltip" offset={6}>
        {label}
      </AriaTooltip>
    </TooltipTrigger>
  );
}
